/**
 * Settings panel for enabling / disabling Org Brain for the current
 * tenant. Captures the consent version + records the admin who flipped
 * the switch. Closes gap B8 (Settings UI half).
 */

import { useState } from 'react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import {
  useAcknowledgeOrgBrain,
  useDisableOrgBrain,
  useEnableOrgBrain,
  useOrgBrainConfig,
  useReindexWorkspace,
} from './hooks';
import { OrgBrainStatus } from './OrgBrainStatus';

// Bump this when the privacy/consent language changes. Admins must
// re-acknowledge to surface the new text (see /enable route). Also
// used as the real `consent_version` overlay for the D2 acknowledge
// flow when an admin clicks through the auto-enable banner — that
// overwrites the platform-generated `auto-on-upgrade-pro-v1` marker.
const CURRENT_CONSENT_VERSION = '2026-04-22';
// TODO: link Org Brain processing purposes doc once published

interface Props {
  tenantId: string;
}

export default function OrgBrainSettings({ tenantId }: Props) {
  const { data: config, isLoading } = useOrgBrainConfig(tenantId);
  const enable = useEnableOrgBrain(tenantId);
  const disable = useDisableOrgBrain(tenantId);
  const acknowledge = useAcknowledgeOrgBrain(tenantId);
  const reindex = useReindexWorkspace(tenantId);
  const [consent, setConsent] = useState(false);
  const [retention, setRetention] = useState<number>(90);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Card className="p-6">
        <Spinner />
      </Card>
    );
  }

  const isEnabled = config?.is_enabled ?? false;
  const consentOutdated =
    isEnabled && config?.consent_version !== CURRENT_CONSENT_VERSION;
  // D2 — auto-enabled but not yet acknowledged. The banner takes
  // precedence over the "consent outdated" flow because asking an admin
  // to re-acknowledge a brand-new auto-enable AND a separate ToS bump
  // in the same render is confusing; once they acknowledge, the
  // consent_version is current and consentOutdated becomes false too.
  const needsAcknowledgement = Boolean(
    isEnabled && config?.auto_enabled && !config?.acknowledged_at,
  );

  async function handleAcknowledge() {
    setError(null);
    try {
      await acknowledge.mutateAsync({
        consent_version: CURRENT_CONSENT_VERSION,
      });
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || 'Failed to acknowledge',
      );
    }
  }

  async function handleEnable() {
    setError(null);
    if (!consent) {
      setError('You must acknowledge the privacy terms to enable Org Brain.');
      return;
    }
    try {
      await enable.mutateAsync({
        consent_version: CURRENT_CONSENT_VERSION,
        runtime_retention_days: retention,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to enable');
    }
  }

  async function handleDisable() {
    setError(null);
    if (!confirm('Disable Org Brain? Graph data is retained; admins can re-enable later.')) {
      return;
    }
    try {
      await disable.mutateAsync();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to disable');
    }
  }

  async function handleReindex() {
    setError(null);
    try {
      await reindex.mutateAsync();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          'Failed to index workspace',
      );
    }
  }

  return (
    <Card className="p-6 space-y-4">
      <header className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Organization Brain</h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            An AI assistant that understands how your workspace is set up. Once
            it&apos;s on, admins can ask everyday questions about your pipelines,
            rules, and SLAs, see what a change will affect before saving it, and
            get early warnings when something looks misconfigured. Sensitive
            details like names, emails, and phone numbers are automatically
            hidden before anything is saved — no customer data is ever stored in
            its original form.
          </p>
        </div>
        <div className="text-right">
          <span
            className={`inline-block px-2 py-1 text-xs rounded ${
              isEnabled
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {isEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </header>

      {needsAcknowledgement && (
        <div
          className="p-4 rounded border border-amber-300 bg-amber-50 text-amber-900 space-y-3"
          role="alert"
          aria-live="polite"
        >
          <div className="font-medium">
            Org Brain was enabled with your Professional plan.
          </div>
          <div className="text-sm">
            We&apos;ve switched on the per-tenant knowledge graph so your team
            gets immediate value from your subscription. Review the data we
            process and acknowledge to confirm — this replaces the placeholder
            consent record on your account with your explicit acknowledgement.
            Auto-enabled at:{' '}
            {config?.enabled_at?.slice(0, 19).replace('T', ' ') ?? '—'} UTC.
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleAcknowledge}
              disabled={acknowledge.isPending}
            >
              {acknowledge.isPending ? 'Acknowledging…' : 'I acknowledge'}
            </Button>
            <Button
              variant="outline"
              onClick={handleDisable}
              disabled={disable.isPending}
            >
              {disable.isPending ? 'Disabling…' : 'Turn off Org Brain'}
            </Button>
          </div>
        </div>
      )}

      {isEnabled && (
        <>
          <OrgBrainStatus tenantId={tenantId} />
          <div className="text-sm text-gray-700 grid grid-cols-2 gap-3">
            <div>
              <div className="text-gray-500">Enabled</div>
              <div>{config?.enabled_at?.slice(0, 19).replace('T', ' ')} UTC</div>
            </div>
            <div>
              <div className="text-gray-500">Consent version</div>
              <div>{config?.consent_version ?? '—'}</div>
            </div>
            <div>
              <div className="text-gray-500">Retention (days)</div>
              <div>{config?.runtime_retention_days}</div>
            </div>
            <div>
              <div className="text-gray-500">Consented at</div>
              <div>{config?.consented_at?.slice(0, 19).replace('T', ' ')} UTC</div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Index your workspace</div>
                <div className="text-xs text-gray-500 max-w-md mt-0.5">
                  Scan this workspace&apos;s pipelines, rules, SLAs, ticket
                  types, and articles into the graph so Ask and health checks
                  have something to reason over. Run this after major config
                  changes.
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleReindex}
                disabled={reindex.isPending}
              >
                {reindex.isPending ? 'Indexing…' : 'Index / refresh'}
              </Button>
            </div>
            {reindex.data && (
              <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 rounded px-3 py-2">
                Indexed {reindex.data.scraped}{' '}
                {reindex.data.scraped === 1 ? 'item' : 'items'} from your
                workspace. The status above will refresh shortly.
              </div>
            )}
          </div>
        </>
      )}

      {consentOutdated && (
        <div className="p-3 text-sm rounded bg-amber-50 text-amber-900">
          Our privacy terms for Org Brain have been updated. Re-acknowledge
          below to keep the feature active. No data is lost in the meantime.
        </div>
      )}

      {!isEnabled || consentOutdated ? (
        <div className="space-y-3 pt-2">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1"
            />
            <span>
              I understand that Org Brain will map how this workspace is set up,
              that personal details like names, emails, and phone numbers are
              hidden before anything is stored, and that I can review an activity
              log and delete this data at any time.
            </span>
          </label>
          <div className="text-sm">
            <label className="flex items-center gap-2">
              <span className="w-56 text-gray-600">Keep indexed data for (days)</span>
              <input
                type="number"
                min={7}
                max={3650}
                value={retention}
                onChange={(e) => setRetention(Number(e.target.value) || 90)}
                className="border rounded px-2 py-1 w-24"
              />
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Org Brain automatically deletes anything older than this. Most
              teams leave it at 90 days.
            </p>
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
              {error}
            </div>
          )}
          <Button
            onClick={handleEnable}
            disabled={!consent || enable.isPending}
          >
            {enable.isPending
              ? 'Enabling…'
              : consentOutdated
                ? 'Re-acknowledge & Enable'
                : 'Enable Org Brain'}
          </Button>
        </div>
      ) : (
        <div className="pt-2">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2 mb-3">
              {error}
            </div>
          )}
          <Button
            variant="outline"
            onClick={handleDisable}
            disabled={disable.isPending}
          >
            {disable.isPending ? 'Disabling…' : 'Disable Org Brain'}
          </Button>
        </div>
      )}
    </Card>
  );
}
